<template>
	<div class="video-embed" :style="{ '--video-embed-aspect': aspect }">
		<button
			v-if="!isActivated"
			type="button"
			class="video-embed-poster"
			:aria-label="playLabel"
			@click="activate"
		>
			<BlogImage
				class="video-embed-thumbnail"
				:src="posterSource"
				:fallback-src="posterFallback"
				:alt="title"
				:aspect="aspect"
			/>
			<span class="video-embed-play" aria-hidden="true">
				<svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
			</span>
			<span v-if="duration" class="video-embed-duration" aria-hidden="true">{{ duration }}</span>
		</button>

		<iframe
			v-else
			class="video-embed-frame"
			:src="embedSource"
			:title="title"
			loading="lazy"
			allow="autoplay; encrypted-media; picture-in-picture; web-share"
			allowfullscreen
		></iframe>
	</div>
</template>

<script setup>
import { computed, ref } from "vue";
import BlogImage from "@/components/blog/BlogImage.vue";

const props = defineProps({
	youTubeId: {
		type: String,
		required: true,
	},
	title: {
		type: String,
		default: "",
	},
	aspect: {
		type: String,
		default: "16 / 9",
	},
	thumbnail: {
		type: String,
		default: "",
	},
	duration: {
		type: String,
		default: "",
	},
	start: {
		type: Number,
		default: 0,
	},
});

const isActivated = ref(false);

const posterSource = computed(() => props.thumbnail || `https://i.ytimg.com/vi/${props.youTubeId}/maxresdefault.jpg`);
const posterFallback = computed(() => `https://i.ytimg.com/vi/${props.youTubeId}/hqdefault.jpg`);
const playLabel = computed(() => (props.title ? `Play video: ${props.title}` : "Play video"));
const embedSource = computed(() => {
	const startParameter = props.start > 0 ? `&start=${props.start}` : "";
	return `https://www.youtube-nocookie.com/embed/${props.youTubeId}?autoplay=1&rel=0&playsinline=1${startParameter}`;
});

function activate() {
	isActivated.value = true;
}
</script>

<style scoped>
.video-embed {
	position: relative;
	width: 100%;
	overflow: hidden;
	border-radius: 1rem;
	background-color: #0f172a;
	aspect-ratio: var(--video-embed-aspect, 16 / 9);
}

.video-embed-poster {
	position: absolute;
	inset: 0;
	display: block;
	width: 100%;
	height: 100%;
	padding: 0;
	border: none;
	background: none;
	cursor: pointer;
}

.video-embed-thumbnail {
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
}

.video-embed-poster::after {
	content: "";
	position: absolute;
	inset: 0;
	background: linear-gradient(180deg, rgba(15, 23, 42, 0) 45%, rgba(15, 23, 42, 0.5) 100%);
	opacity: 0;
	transition: opacity 0.25s ease;
}

.video-embed-poster:hover::after,
.video-embed-poster:focus-visible::after {
	opacity: 1;
}

.video-embed-play {
	position: absolute;
	top: 50%;
	left: 50%;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 4.5rem;
	height: 4.5rem;
	border-radius: 999px;
	background: rgba(255, 255, 255, 0.94);
	color: #2563eb;
	box-shadow: 0 14px 30px -12px rgba(15, 39, 66, 0.6);
	transform: translate(-50%, -50%);
	transition: transform 0.2s ease, background 0.2s ease, color 0.2s ease;
	z-index: 1;
}

.video-embed-play svg {
	width: 2rem;
	height: 2rem;
	margin-left: 0.18rem;
}

.video-embed-poster:hover .video-embed-play,
.video-embed-poster:focus-visible .video-embed-play {
	transform: translate(-50%, -50%) scale(1.08);
	background: #2563eb;
	color: #ffffff;
}

.video-embed-poster:focus-visible {
	outline: 3px solid rgba(37, 99, 235, 0.55);
	outline-offset: -3px;
}

.video-embed-duration {
	position: absolute;
	right: 0.75rem;
	bottom: 0.75rem;
	padding: 0.2rem 0.55rem;
	border-radius: 0.4rem;
	background: rgba(15, 23, 42, 0.85);
	color: #ffffff;
	font-family: "Poppins", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
	font-size: 0.78rem;
	font-weight: 600;
	letter-spacing: 0.02em;
	z-index: 1;
}

.video-embed-frame {
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	border: 0;
}

.video-embed-poster:hover :deep(.blog-image-img),
.video-embed-poster:focus-visible :deep(.blog-image-img) {
	-webkit-transform: scale(1.04);
	transform: scale(1.04);
}
</style>